import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Download, Trash2 } from 'lucide-react'
import { Banner, Button, Card, Field, Row, Segmented, Sheet } from '../ui'
import { useAuth } from '../lib/auth'
import { supabase } from '../lib/supabase'
import { feedKey, recipeOf, useActiveTaste, useAlerts, useHarvestRuns, useRecipes, useVenues, useWrongReports } from '../lib/queries'
import { useAcknowledgeAlert, useSaveTaste, useUpdateProfile } from '../lib/mutations'
import { useProfile, type Profile } from '../lib/profile'
import { profileKey } from '../lib/queries'
import { maskIcsUrl, normaliseIcsUrl } from '../../../src/icsUrl'
import { useChatSpend } from '../lib/chat'
import { getTheme, setTheme, type ThemeChoice } from '../lib/theme'
import { HealthStrip, type LastRun } from './HealthStrip'
import { ProfileForm } from './ProfileForm'
import { RegionSection } from './RegionSection'
import { useCurrentRegion } from '../lib/currentRegion'
import { TasteForm } from './TasteForm'
import { canEditTaste, draftFromVector, isTasteChanged, vectorFrom, type TasteDraft } from '../lib/tasteForm'
import { draftFrom, isChanged, patchFrom, type Draft } from '../lib/onboarding'
import { useIsAdmin, adminGate } from '../lib/useIsAdmin'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

const Notes = lazy(() => import('./Notes').then(m => ({ default: m.Notes })))
const VenueAdmin = lazy(() => import('./VenueAdmin').then(m => ({ default: m.VenueAdmin })))

/** One identity for "nothing yet", so the memos below do not recompute on a
 *  fresh empty array every render — the same trick `Home.tsx` uses for the
 *  same reason. */
const NONE_YET: never[] = []

const MINUTE = 60_000

function formatAgo(ms: number): string {
  const mins = Math.round(ms / MINUTE)
  if (mins < 60) return `${Math.max(mins, 1)} min ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours} h ago`
  return `${Math.round(hours / 24)} d ago`
}

/** True once, kept live by the two events a browser actually fires — never
 *  polled. `navigator.onLine` can lie in either direction (a captive portal
 *  reads "online"), but it is the only signal cheap enough for a status line;
 *  the mutations it gates behave correctly either way, since a write that
 *  turns out to have no connection still just pauses. Moved here from
 *  `App.tsx` with WP 2.3, when the shell's top bar became just the wordmark
 *  and the account circle (WP 10.1 then removed the bar altogether), so the
 *  one line X11 added lives where the health strip it sits beside now
 *  lives, under System. */
function useOnline(): boolean {
  const [online, setOnline] = useState(() => navigator.onLine)
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => { window.removeEventListener('online', on); window.removeEventListener('offline', off) }
  }, [])
  return online
}

/** The current region's feed's own `dataUpdatedAt` (`['feed', regionId]`, C2.2),
 *  read off the query cache directly rather than through `useFeed()` — that
 *  would select and hold the whole derived feed just to read one timestamp off
 *  it. Subscribed rather than polled: the cache only changes when a fetch
 *  actually lands, and this is the one place that needs to know. */
function useFeedSyncedAt(): number | null {
  const qc = useQueryClient()
  const regionId = useCurrentRegion().region.id
  const [at, setAt] = useState(() => qc.getQueryState(feedKey(regionId))?.dataUpdatedAt ?? null)
  useEffect(() => {
    // Only the feed's own events, and only when the stamp moved. The cache
    // emits for every query — including `observerAdded` while a child such as
    // HealthStrip is still rendering its first `useIsAdmin()` (U0.3) — and a
    // setState here during another component's render is React's
    // "cannot update a component while rendering a different component".
    return qc.getQueryCache().subscribe(e => {
      if (e.query.queryKey[0] !== 'feed' || e.query.queryKey[1] !== regionId) return
      const next = qc.getQueryState(feedKey(regionId))?.dataUpdatedAt ?? null
      setAt(prev => (prev === next ? prev : next))
    })
  }, [qc, regionId])
  return at || null
}

/**
 * X11: the one line the offline package adds. `null` — nothing shown — unless
 * the device is offline or the feed hasn't synced within the hour; reads
 * already work from the persisted cache either way, so this is the only
 * thing on screen that needs to say so.
 *
 * `tick` exists purely to re-render as the age crosses a minute boundary —
 * nothing it holds is read, so a `useState` a passive listener would set
 * looks the same to the type checker and is not the same thing: this one
 * really has nothing else to trigger the re-render otherwise.
 */
function useSyncStatus(): string | null {
  const online = useOnline()
  const syncedAt = useFeedSyncedAt()
  const [, tick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => tick(t => t + 1), MINUTE)
    return () => clearInterval(id)
  }, [])
  const staleMs = syncedAt === null ? null : Date.now() - syncedAt
  if (online && (staleMs === null || staleMs < 60 * MINUTE)) return null
  if (staleMs === null) return null
  return `Updated ${formatAgo(staleMs)}`
}

/**
 * Where a passkey gets created, and the only way out of the app.
 *
 * Both of these existed in `lib/auth.tsx` and neither was reachable, which
 * left the passkey flow unable to start: the sign-in screen offers "Sign in
 * with a passkey", and that needs one to already exist. The comment on
 * SignIn.tsx says password sign-in is "how the first passkey gets registered
 * in the first place" — and the registering half was never built.
 *
 * Behind the account button at the end of every tab's first row
 * (`ScreenHead`, WP 10.1) rather than a sixth tab. These are things you do
 * once, and the five tabs were settled by the prototype.
 */
export function AccountSheet({ onClose }: { onClose: () => void }) {
  return (
    <Sheet title="Account" closeButton open onOpenChange={open => { if (!open) onClose() }}>
      <AccountBody onClose={onClose} />
    </Sheet>
  )
}

/**
 * U5.2: a person's own data, out and gone. The export is the Worker's
 * `GET /me/export` (the service key reads every personal table; the client
 * only holds a session) handed over as a file the way `EventActions`'s
 * `.ics` is: a blob behind a temporary link.
 */
async function downloadExport(): Promise<string | null> {
  if (!WORKER_URL) return 'VITE_WORKER_URL is not set'
  const { data: { session } } = await supabase.auth.getSession()
  const token = session?.access_token
  if (!token) return 'Not signed in'
  let res: Response
  try {
    res = await fetch(`${WORKER_URL}/me/export`, { headers: { authorization: `Bearer ${token}` } })
  } catch {
    return 'No connection.'
  }
  if (!res.ok) return `The export failed (${res.status}). Try again.`
  const blob = new Blob([await res.text()], { type: 'application/json' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `kiez-export-${new Date().toISOString().slice(0, 10)}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 1000)
  return null
}

/** Two rows under "Your data". An admin's account is refused by the Worker
 *  (403), so the delete row is not drawn for one: a row that can only fail is
 *  worse than none. Unknown (`undefined`) draws as not an admin, as above. */
function YourData({ email, admin }: { email: string, admin: boolean }) {
  const { deleteAccount } = useAuth()
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState(false)
  const [typed, setTyped] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const matches = email !== '' && typed.trim().toLowerCase() === email.toLowerCase()

  async function exportNow() {
    setExporting(true)
    setError(null)
    setError(await downloadExport())
    setExporting(false)
  }

  async function deleteNow() {
    setDeleting(true)
    setDeleteError(null)
    // On success the sign-out unmounts the whole app; nothing to reset.
    const err = await deleteAccount(typed.trim())
    if (err) { setDeleteError(err); setDeleting(false) }
  }

  return (
    <section>
      <div className="rowhead"><h2>Your data</h2></div>
      <Card>
        <Row
          className="u-row--list u-row--flush"
          leading={<Download size={20} />}
          title="Export my data"
          meta="Everything Kiez holds about you, as one file"
          disabled={exporting}
          onPress={() => void exportNow()}
        />
        {!admin && (
          <Row
            className="u-row--list u-row--flush"
            leading={<Trash2 size={20} />}
            title="Delete my account"
            meta="Removes you and everything above, for good"
            onPress={() => { setTyped(''); setDeleteError(null); setConfirming(true) }}
          />
        )}
      </Card>
      {error && <Banner tone="bad">{error}</Banner>}

      {confirming && (
        <Sheet open onOpenChange={o => { if (!o && !deleting) setConfirming(false) }} title="Delete my account"
          actions={
            <>
              <Button variant="primary" busy={deleting} disabled={!matches || deleting} onClick={() => void deleteNow()}>
                Delete my account
              </Button>
              <Button variant="secondary" disabled={deleting} onClick={() => setConfirming(false)}>Cancel</Button>
            </>
          }>
          <div className="stack">
            <p className="detail">
              This deletes your account and everything in it: lists, saved and hidden events, stays,
              flights, notes, Ask history. It cannot be undone. Export first if you want a copy.
            </p>
            <Field label={`Type ${email} to confirm`}>
              <Field.Input
                value={typed}
                autoFocus
                type="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                onChange={e => setTyped(e.target.value)}
              />
            </Field>
            {deleteError && <Banner tone="bad">{deleteError}</Banner>}
          </div>
        </Sheet>
      )}
    </section>
  )
}

/**
 * U3.1: a calendar, by its private link. Google, iCloud, Outlook and Nextcloud
 * each publish a "secret address in iCal format"; the Worker reads it every
 * night into the Year screen's commitments (`src/ics-run.ts`). Read-only, no
 * sign-in to anything.
 *
 * The address is a secret, so once saved it is drawn masked (host only) and
 * "Replace" opens an empty field rather than the old value. The two things the
 * Worker says back — when it last worked, and why it did not — are its columns
 * (`calendar_synced_at`, `calendar_sync_note`), shown, never written, here.
 *
 * Saving a new address clears both server-side (a trigger), but the cached
 * profile still holds the old pair until it refetches, so `fresh` hides the
 * status line for this visit and the profile query is invalidated once the
 * write lands — a reload then reads the cleared row, not the stale note.
 */
function CalendarFeed() {
  const qc = useQueryClient()
  const { session } = useAuth()
  const uid = session?.user.id
  const profile = useProfile()
  const { mutate, isPending } = useUpdateProfile()
  const saved = profile.calendar_ics_url ?? null
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState<string | undefined>()
  const [fresh, setFresh] = useState<'saved' | 'removed' | null>(null)

  function write(url: string | null) {
    if (!uid) return
    mutate({ uid, patch: { calendar_ics_url: url } }, {
      onSettled: () => { void qc.invalidateQueries({ queryKey: profileKey(uid) }) },
    })
    setDraft('')
    setEditing(false)
    setError(undefined)
    setFresh(url ? 'saved' : 'removed')
  }

  function save() {
    const checked = normaliseIcsUrl(draft)
    if (!checked.ok) { setError(checked.note); return }
    write(checked.url)
  }

  const form = !saved || editing
  const syncedAt = profile.calendar_synced_at ? Date.parse(profile.calendar_synced_at) : NaN
  const status = fresh === 'saved'
    ? 'Saved. The first sync runs overnight.'
    : fresh === 'removed'
      ? 'Removed, along with the events it brought in.'
      : [
          saved && (Number.isFinite(syncedAt) ? `Synced ${formatAgo(Date.now() - syncedAt)}.` : 'Not synced yet; it runs overnight.'),
          saved && profile.calendar_sync_note,
        ].filter(Boolean).join(' ')

  return (
    <section>
      <div className="rowhead"><h2>Calendar</h2></div>
      <div className="stack">
        {form ? (
          <>
            <Field
              label="Calendar link"
              hint="The private iCal address: Google Calendar → Settings → Integrate calendar → Secret address in iCal format. iCloud, Outlook and Nextcloud have an equivalent. Read-only; synced every night."
              error={error}
            >
              <Field.Input
                value={draft}
                type="url"
                inputMode="url"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="off"
                spellCheck={false}
                placeholder="https://…"
                onChange={e => { setDraft(e.target.value); setError(undefined) }}
                onKeyDown={e => { if (e.key === 'Enter') save() }}
              />
            </Field>
            <div className="stack">
              <Button variant="secondary" busy={isPending} disabled={!draft.trim()} onClick={save}>Save link</Button>
              {editing && <Button variant="quiet" onClick={() => { setEditing(false); setDraft(''); setError(undefined) }}>Cancel</Button>}
            </div>
          </>
        ) : (
          <Card>
            <Row
              className="u-row--list u-row--flush"
              leading={null}
              title="Calendar link saved"
              meta={maskIcsUrl(saved ?? '')}
              chevron="right"
              onPress={() => { setEditing(true); setFresh(null) }}
            />
            <Row
              className="u-row--list u-row--flush"
              leading={null}
              title="Remove link"
              meta="Stops the sync and removes the events it brought in"
              onPress={() => write(null)}
            />
          </Card>
        )}
        {status && <p className="note">{status}</p>}
      </div>
    </section>
  )
}

/** "Match device", not "System": the row above is already called System,
 *  and two "System"s a hand apart read as one control. */
const THEMES = [
  { value: 'system', label: 'Match device' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
]

/**
 * U1.4: the first-sign-in form, kept under Account as "You". The same
 * `ProfileForm`, with a Save instead of Done: it writes the draft as it is
 * left (`patchFrom`) and nothing else, so it cannot move `onboarded_at`.
 * The draft starts from the profile and is not re-seeded while it is open —
 * a refetch under the person's thumbs must not undo what they are typing.
 * The one exception is the row arriving at all: until it has, `useProfile()`
 * is `DEFAULT_PROFILE` (user_id ''), a draft seeded from it would be blank,
 * and Save would write those blanks over the real row. So the form is keyed
 * on `user_id` and is built again when the real row replaces the default.
 */
function YouSection() {
  const profile = useProfile()
  return <YouForm key={profile.user_id} profile={profile} />
}

function YouForm({ profile }: { profile: Profile }) {
  const { updateProfile } = useUpdateProfile()
  const [draft, setDraft] = useState<Draft>(() => draftFrom(profile))
  const [saved, setSaved] = useState(false)
  const changed = isChanged(draft, profile)
  return (
    <section>
      <div className="rowhead"><h2>You</h2></div>
      <div className="stack">
        <ProfileForm draft={draft} onChange={d => { setDraft(d); setSaved(false) }} />
        <Button block disabled={!changed} onClick={() => { updateProfile(patchFrom(draft)); setSaved(true) }}>
          Save
        </Button>
        {saved && !changed && <p className="detail" role="status">Saved.</p>}
      </div>
    </section>
  )
}

/**
 * U2.4: "Your taste", the same questions as onboarding's second step.
 * Drawn only once the active-vector read has settled (a pending read must not
 * flash a blank form over answers that are about to arrive) and only when the
 * form can reproduce what is saved: nothing, or a 2.0 vector. Doug's is a 1.1
 * document written by hand; this section is not drawn for it, and the RPC
 * would refuse it besides (`canEditTaste`).
 */
function TasteSection() {
  const profile = useProfile()
  const tasteQ = useActiveTaste()
  if (!tasteQ.isSuccess || !profile.user_id || !canEditTaste(tasteQ.data)) return null
  return <TasteSectionForm key={profile.user_id} englishOnly={profile.english_only} vector={tasteQ.data?.vector} />
}

function TasteSectionForm({ englishOnly, vector }: { englishOnly: boolean; vector: unknown }) {
  const venuesQ = useVenues()
  const save = useSaveTaste()
  // The draft starts from what is saved and is not re-seeded while open, as
  // the You form is not; `base` follows the cache, so after a save the form
  // reads as unchanged.
  const [draft, setDraft] = useState<TasteDraft>(() => draftFromVector(vector))
  const base = useMemo(() => draftFromVector(vector), [vector])
  const [error, setError] = useState(false)
  const changed = isTasteChanged(draft, base, englishOnly)
  return (
    <section>
      <div className="rowhead"><h2>Your taste</h2></div>
      <div className="stack">
        <TasteForm draft={draft} englishOnly={englishOnly} onChange={d => { setDraft(d); setError(false) }} />
        <Button block disabled={!changed} busy={save.isPending}
                onClick={() => {
                  setError(false)
                  save.mutate({ vector: vectorFrom(draft, englishOnly, venuesQ.data?.list ?? []) }, { onError: () => setError(true) })
                }}>
          Save
        </Button>
        {error && <Banner tone="bad">Couldn't save your answers. Try again.</Banner>}
        {save.isSuccess && !changed && !error && <p className="detail" role="status">Saved.</p>}
      </div>
    </section>
  )
}

/**
 * What the sheet holds, without the sheet: the desk's Account surface draws
 * this same body inside `.desk-phone` (a4-03), so the two cannot drift.
 */
export function AccountBody({ onClose }: { onClose?: () => void } = {}) {
  const { session, registerPasskey, signOut } = useAuth()
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [confirmingSignOut, setConfirmingSignOut] = useState(false)
  const [venueAdmin, setVenueAdmin] = useState(false)
  // U0.3: Venue admin and System (recipes, alerts, spend) are for admins.
  // `undefined` — the `app_admins` read still in flight — draws as not an
  // admin, so nobody sees either row flash in on a cold load.
  const admin = adminGate(useIsAdmin()) === 'show'
  // `lib/theme.ts` had `setTheme` and no caller: the scheme followed the
  // device and nothing could say otherwise. Per device, as the store is.
  const [theme, setThemeState] = useState<ThemeChoice>(getTheme)

  // N1 (modify): the health strip moves here from Home, collapsed by default
  // — "make sure your notifications are pretty busy in there" is not.
  const [systemOpen, setSystemOpen] = useState(false)
  const syncStatus = useSyncStatus()
  const recipesQ = useRecipes()
  const alertsQ = useAlerts()
  const runsQ = useHarvestRuns()
  const wrongQ = useWrongReports()
  const recipes = recipesQ.data ?? NONE_YET
  const alerts = alertsQ.data ?? NONE_YET
  const runs = runsQ.data ?? NONE_YET
  const wrong = wrongQ.data ?? NONE_YET
  const ackM = useAcknowledgeAlert()

  // Same shape Home.tsx built for the strip before this package moved it —
  // duplicated rather than shared, because Home.tsx's own copy of these
  // queries and memos is out of this package's files (it still needs them
  // for its own loading gate) and every query key here is already in the
  // store, so this costs nothing extra to fetch.
  const repairSpend = useMemo(
    () => runs.reduce((n, r) => n + Number(r.model_cost_usd ?? 0), 0), [runs])

  // WP 9.5: the butler's own spend, the same "this week" the line above it
  // reads, under the repair-spend figure rather than folded into it — the
  // Worker's harvest and the butler bill against the same account but are
  // two different caps (`DAY_CAP_USD` vs the harvest's own ceilings), and a
  // single combined number would answer a question ("did the butler run
  // over its cap") nobody asked while hiding the one somebody did.
  const chatSpendUsd = useChatSpend()

  const lastRun = useMemo<LastRun>(() => {
    const m: LastRun = new Map()
    for (const r of runs) {
      if (!r.recipe_id || m.has(r.recipe_id)) continue
      m.set(r.recipe_id, { rows_valid: r.rows_valid, status: r.status, started_at: r.started_at })
    }
    return m
  }, [runs])

  // Same shape as before — recipe to count — off a different table. WP 2.4
  // moved the report from `taste_signal` to `app_notes`, so the recipe comes
  // from the entry the note names rather than from a denormalised `features`
  // blob; a report whose entry has since been re-harvested away has no recipe
  // to blame and is skipped, which is what the old `features.recipe` check did
  // for a signal written before the column existed.
  const wrongThisWeek = useMemo(() => {
    const m = new Map<string, number>()
    for (const report of wrong) {
      const recipe = recipeOf(report)
      if (recipe) m.set(recipe, (m.get(recipe) ?? 0) + 1)
    }
    return m
  }, [wrong])

  /**
   * Passkeys this account already has.
   *
   * `done` only knew about a passkey added in this session, so the row went on
   * offering "Add a passkey" on every device that already had one — which is
   * the one place the answer is knowable and was never asked for. `null` while
   * the question is outstanding, so the row says nothing rather than guessing
   * and then correcting itself.
   */
  const [existing, setExisting] = useState<number | null>(null)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const { data } = await supabase.auth.passkey.list()
        if (live) setExisting(data?.length ?? 0)
      } catch {
        // The experimental flag being off is not worth an error on this
        // screen: the row falls back to offering the add, which is what it
        // did before there was a way to ask.
        if (live) setExisting(0)
      }
    })()
    return () => { live = false }
  }, [])

  const hasPasskey = done || (existing ?? 0) > 0

  async function addPasskey() {
    setBusy(true)
    setMessage(null)
    const error = await registerPasskey()
    setBusy(false)
    if (error) {
      // Worth being specific: the two likely failures are the dashboard
      // toggle being off and being on an origin the Relying Party ID does
      // not cover, and neither error says so on its own.
      setMessage(
        `${error} — check passkeys are enabled in Supabase, and that you are on ` +
          `the deployed app rather than localhost.`,
      )
      return
    }
    setDone(true)
    setMessage('Passkey saved. Next time, use "Sign in with a passkey".')
  }

  return (
    <>
      <div className="stack">
      {/* a2-34 — Doug chose: the passkey row becomes a plain status line
          once one exists. It was a disabled row, "Passkey added / Sign in
          with Face ID or a fingerprint", drawn like the two rows beside it
          that open something — a state that read as an action. Nothing at
          all while the answer is in flight, as before. */}
      <p className="detail">
        Signed in as {session?.user?.email ?? 'unknown'}.
        {existing !== null && hasPasskey && (
          <><br />{existing > 1 ? `${existing} passkeys set up.` : 'Passkey set up.'}</>
        )}
      </p>

      <Card>
        {/* Nothing at all while the answer is still in flight: a row that says
            "Add a passkey" and then changes its mind is worse than a beat of
            silence. */}
        {existing !== null && !hasPasskey && (
          <Row
            className="u-row--list u-row--flush"
            leading={null}
            title="Add a passkey"
            meta="So you do not need the password on this device"
            disabled={busy}
            onPress={() => void addPasskey()}
          />
        )}

        {/* Paste a hero, edit what a venue is, read what sources it — see
            app/src/components/VenueAdmin.tsx. Here rather than a sixth tab:
            it is upkeep, not a thing to visit daily. */}
        {admin && (
          <Row
            className="u-row--list u-row--flush"
            leading={null}
            title="Venue admin"
            meta="Photos, metadata and where a venue's events come from"
            chevron="right"
            onPress={() => setVenueAdmin(true)}
          />
        )}

        {/* N1 (modify): moved off Home, collapsed by default. The subtitle is
            the one line X11 added to the old top bar (`syncStatus`) — this is
            the closest thing left to a status line — WP 10.1 removed the
            top bar itself. */}
        {admin && (
          <Row
            className="u-row--list u-row--flush"
            leading={null}
            title="System"
            meta={syncStatus ?? 'Recipes, alerts and repair spend'}
            chevron={systemOpen ? 'down' : 'right'}
            onPress={() => setSystemOpen(v => !v)}
          />
        )}
      </Card>

      {admin && systemOpen && (
        <>
        <HealthStrip
          recipes={recipes}
          alerts={alerts}
          spendUsd={repairSpend}
          wrongThisWeek={wrongThisWeek}
          lastRun={lastRun}
          onAcknowledge={id => ackM.mutate(id)}
        />
        <p className="note">
          {chatSpendUsd > 0 ? `Butler: ${chatSpendUsd >= 1 ? `$${chatSpendUsd.toFixed(2)}` : `${Math.round(chatSpendUsd * 100)}¢`} this week` : 'Butler: nothing this week'}
        </p>
        </>
      )}

      {message && (done
        ? <p className="detail">{message}</p>
        : <Banner tone="bad">{message}</Banner>)}

      <YouSection />

      {/* C2.2: which region the app shows. Under You — it is a preference of
          this device, a sibling of the home pin the form above sets. A switch
          remounts the app (`RegionRoot`), which closes this sheet when the
          region changes; `onChosen` closes it when it does not. */}
      <RegionSection onChosen={onClose} />

      <TasteSection />

      <Segmented
        aria-label="Theme"
        value={theme}
        onChange={v => { const t = v as ThemeChoice; setTheme(t); setThemeState(t) }}
        options={THEMES}
      />

      <CalendarFeed />

      <Suspense fallback={null}>
        <Notes />
      </Suspense>

      <YourData email={session?.user?.email ?? ''} admin={admin} />

      {/* Which build this device is actually running. An installed PWA can sit
          on an old bundle without anything on screen saying so, and "it is not
          there" is impossible to diagnose without it. */}
      <p className="note">
        Build {__BUILD_ID__}
      </p>

      {/* Two taps. Signing out of an installed app you get back into with a
          password you may not have to hand is not a thing to do by accident. */}
      <Button
        variant="quiet"
        block
        onClick={() => {
          if (confirmingSignOut) void signOut()
          else setConfirmingSignOut(true)
        }}
      >
        {confirmingSignOut ? 'Tap again to sign out' : 'Sign out'}
      </Button>
      </div>
    {/* Stacked on top rather than swapped in: closing it should return to
        Account, not to the app underneath. */}
    <Suspense fallback={null}>
      {admin && venueAdmin && <VenueAdmin onClose={() => setVenueAdmin(false)} />}
    </Suspense>
    </>
  )
}
